import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { TextSpan, fontWeights, fontSizes, truncateCss } from 'app/typography';
import { layers } from 'app/spacing';

export const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const StyledFieldsetLabel = styled(TextSpan)`
  color: var(--color-font-placeholder);
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.small};
  // Put the label exactly 10px above the checkbox.
  margin-bottom: 2px;
  max-width: 100%;
  ${truncateCss}
  ${({ standaloneLabel }) =>
    standaloneLabel &&
    css`
      margin-bottom: 8px;
      margin-top: 5px;
    `}
`;

export const StyledFieldsetOutlineLabel = styled(TextSpan)`
  color: ${'var(--color-font-primary)'};
  margin-bottom: 12px;
`;

// when displaying a single checkbox, we want the label to appear as if it were a fieldset label
// While also being able to toggle the checkbox on interaction
const standaloneLabelStyles = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 0;
  margin-bottom: 8px;
`;

export const StyledLabel = styled.label`
  display: inline-flex;
  align-items: center;
  margin: ${({ size, withIcon }) =>
    size === 'small'
      ? withIcon
        ? '0 5px 0px 0px'
        : '10px 10px 10px 0'
      : '12px'};

  user-select: none;
  overflow: hidden;
  position: relative;

  ${({ standaloneLabel }) => standaloneLabel && standaloneLabelStyles}

  ${({ inline }) =>
    inline
      ? css`
          margin: 0;
        `
      : ''}
`;

export const StyledInput = styled.input`
  position: absolute;
  opacity: 0;
  z-index: ${layers.content(0)};
`;

export const StyledInputFacade = styled.span`
  position: relative;
  width: ${({ size, underline }) =>
    size === 'small' && underline ? '14px' : '16px'};
  height: ${({ size, underline }) =>
    size === 'small' && underline ? '14px' : '16px'};
  border: 1px solid var(--color-border-dark);
  border-radius: ${({ size, underline }) =>
    size === 'small' && underline ? '1px' : '3px'};

  ${StyledLabel}:hover & {
    border-color: ${(props) =>
      props.error
        ? 'var(--color-button-warning-hover)'
        : 'var(--color-button-secondary-hover)'};
  }

  ${StyledInput}:checked~& {
    background: var(--color-button-secondary-hover);
    border-color: var(--color-button-secondary-hover);

    &::after {
      content: '';
      position: absolute;
      left: 5px;
      top: 1px;
      width: 5px;
      height: 10px;
      border-style: solid;
      border-color: var(--color-background-white);
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
      ${({ size, underline }) =>
        size === 'small' &&
        underline &&
        css`
          left: 4px;
          top: 0px;
          transform: rotate(45deg) scale(0.875);
        `}
    }
  }

  ${(props) =>
    props.indeterminate &&
    css`
      ${StyledInput}~& {
        background: var(--color-button-secondary-hover);
        border-color: var(--color-button-secondary-hover) !important;

        &::after {
          content: '';
          position: absolute;
          left: 6px;
          top: 2px;
          width: 0px;
          height: 10px;
          border-style: solid;
          border-color: var(--color-background-white);
          border-width: 0 2px 0 0;
          transform: rotate(90deg);
        }
      }
      ${StyledInput}~& {
        background: var(--color-button-secondary-hover);
        border-color: var(--color-button-secondary-hover);

        &::after {
          content: '';
          position: absolute;
          left: 6px;
          top: 2px;
          width: 0px;
          height: 10px;
          border-style: solid;
          border-color: var(--color-background-white);
          border-width: 0 2px 0 0;
          transform: rotate(90deg);
        }
      }
    `}

  ${StyledInput}:disabled~& {
    border-color: var(--color-border-medium);
    background-color: var(--color-background-light);
    &::after {
      border-color: var(--color-border-dark);
    }
  }

  ${(props) =>
    props.error &&
    css`
      border-color: var(--color-button-warning-hover);
    `}
`;

const shrinkStyles = css`
  // scaling to 78.5% will take the 14px down to the 11px we want
  // The 4px will put the label 10px above the capline of the input text
  transform: translate(0, 4px) scale(0.785);
  position: absolute;
  transform-origin: top left;
`;

export const StyledText = styled(TextSpan)`
  margin-left: 10px;
  color: ${(props) =>
    props.disabled
      ? 'var(--color-font-placeholder)'
      : 'var(--color-font-primary)'};
  ${({ shrink }) => shrink && shrinkStyles};

  ${({ grow }) =>
    grow
      ? ''
      : css`
          // Truncate long labels.
          max-width: 170px;
        `}
  flex: 1; // When too wide, don't shrink checkbox itself
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
